import React, { useState, useCallback } from 'react';
import GoogleMapReact from 'google-map-react';

interface LocationSearchMapProps {
  lat: number;
  long: number;
  onLocationChange?: (lat: number, long: number) => void;
  draggable?: boolean;
  showCircle?: boolean;
  circleRadius?: number; // in km
}

export const LocationSearchMap = ({
  lat,
  long,
  onLocationChange,
  showCircle = false,
  circleRadius = 10,
}: LocationSearchMapProps) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [size, setSize] = React.useState<{ width: number; height: number }>({ width: 0, height: 0 });
  const [mapInstance, setMapInstance] = React.useState<any>(null);
  const markerRef = React.useRef<any>(null);
  const circleRef = React.useRef<any>(null);

  // Ensure valid coordinates with fallbacks
  const validLat = lat && !Number.isNaN(lat) ? lat : -33.8688; // Sydney as fallback
  const validLng = long && !Number.isNaN(long) ? long : 151.2093; // Sydney as fallback

  const [currentLat, setCurrentLat] = useState(validLat);
  const [currentLng, setCurrentLng] = useState(validLng);

  // Calculate zoom level based on radius
  const calculateZoomLevel = (radius: number): number => {
    // Approximate zoom levels for different radius sizes
    if (radius <= 1) return 15;
    if (radius <= 2) return 14;
    if (radius <= 5) return 13;
    if (radius <= 10) return 12;
    if (radius <= 20) return 11;
    if (radius <= 50) return 10;
    if (radius <= 100) return 9;
    return 8;
  };

  // Update container size
  React.useEffect(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setSize({ width: rect.width, height: rect.height });
    }
  }, []);

  const handleMapClick = useCallback(
    (event: any) => {
      if (onLocationChange && event.lat && event.lng) {
        setCurrentLat(event.lat);
        setCurrentLng(event.lng);
        onLocationChange(event.lat, event.lng);

        // Update marker position if using Google Maps API
        if (markerRef.current) {
          markerRef.current.setPosition({ lat: event.lat, lng: event.lng });
        }

        // Update circle position
        if (circleRef.current) {
          circleRef.current.setCenter({ lat: event.lat, lng: event.lng });
        }
      }
    },
    [onLocationChange]
  );

  // Handle API loaded
  const handleApiLoaded = useCallback(
    ({ map, maps }: { map: any; maps: any }) => {
      setMapInstance(map);

      // Create a draggable marker using Google Maps API
      if (maps && map && onLocationChange) {
        // Remove any existing marker
        if (markerRef.current) {
          markerRef.current.setMap(null);
        }

        // Create new marker
        const marker = new maps.Marker({
          position: { lat: currentLat, lng: currentLng },
          map: map,
          draggable: true,
          icon: {
            url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
          },
        });

        // Add drag event listener
        marker.addListener('dragend', (event: any) => {
          const newLat = event.latLng.lat();
          const newLng = event.latLng.lng();
          setCurrentLat(newLat);
          setCurrentLng(newLng);
          onLocationChange(newLat, newLng);

          // Update circle position
          if (circleRef.current) {
            circleRef.current.setCenter({ lat: newLat, lng: newLng });
          }
        });

        markerRef.current = marker;

        // Create circle if needed
        if (showCircle && circleRadius > 0) {
          // Remove existing circle
          if (circleRef.current) {
            circleRef.current.setMap(null);
          }

          const circle = new maps.Circle({
            strokeColor: '#EA4335',
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor: '#EA4335',
            fillOpacity: 0.15,
            map: map,
            center: { lat: currentLat, lng: currentLng },
            radius: circleRadius * 1000, // Convert km to meters
          });

          circleRef.current = circle;
        }
      }
    },
    [currentLat, currentLng, onLocationChange, showCircle, circleRadius]
  );

  // Update position when props change
  React.useEffect(() => {
    const newValidLat = lat && !Number.isNaN(lat) ? lat : -33.8688;
    const newValidLng = long && !Number.isNaN(long) ? long : 151.2093;
    setCurrentLat(newValidLat);
    setCurrentLng(newValidLng);

    // Update marker position
    if (markerRef.current) {
      markerRef.current.setPosition({ lat: newValidLat, lng: newValidLng });
    }

    // Update circle position
    if (circleRef.current) {
      circleRef.current.setCenter({ lat: newValidLat, lng: newValidLng });
    }
  }, [lat, long]);

  // Update circle radius when it changes
  React.useEffect(() => {
    if (circleRef.current && showCircle) {
      circleRef.current.setRadius(circleRadius * 1000);
    }
    // Update map zoom when radius changes
    if (mapInstance && showCircle) {
      const newZoom = calculateZoomLevel(circleRadius);
      mapInstance.setZoom(newZoom);
    }
  }, [circleRadius, showCircle, mapInstance]);

  // Only render map when we have valid size
  if (size.width === 0 || size.height === 0) {
    return <div ref={containerRef} style={{ width: '100%', height: '100%' }} />;
  }

  const zoomLevel = showCircle ? calculateZoomLevel(circleRadius) : 13;

  return (
    <div ref={containerRef} style={{ width: '100%', height: '100%' }}>
      <GoogleMapReact
        bootstrapURLKeys={{ key: 'AIzaSyAtLoNynuMVQAXXvaaxXG5PFB2IXja6f2E' }}
        center={{ lat: currentLat, lng: currentLng }}
        defaultCenter={{ lat: currentLat, lng: currentLng }}
        zoom={zoomLevel}
        defaultZoom={zoomLevel}
        onClick={handleMapClick}
        onGoogleApiLoaded={handleApiLoaded}
        yesIWantToUseGoogleMapApiInternals
      >
        {/* Marker will be added via Google Maps API */}
      </GoogleMapReact>
    </div>
  );
};
